import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Navigation, Rotate3D, ChevronRight } from 'lucide-react';
import gallery from '../data/uzlet-galeria.json';
import store360 from '../data/store360.json';
import Store360Viewer from './Store360Viewer';
import { STORES, storeAddressDisplay } from '../constants/stores';
import { IMAGE_VARIANTS } from '../constants/imageVariants';
import {
  trackStoreGalleryVisible,
  trackStoreGalleryClick,
  trackDirectionsClick,
} from '../utils/analytics';

/**
 * „Nézz be üzleteinkbe” – két üzlet-kártya a főoldalra.
 *
 * MIÉRT NEM a StoreGallery: az üzletenként 5 nézet + 360 csempéből álló rácsot
 * rajzol, mérve **4 553 px magas 390 px-en** (a főoldal 35%-a), és 3 „Fotó
 * hamarosan" placeholdert tartalmaz. Bizalomépítésre a főoldal első harmadában
 * ez pont ellenkező hatású lenne. A StoreGallery az /uzletek oldalon marad,
 * változatlanul.
 *
 * AMI ÚJRAHASZNÁLT: a `Store360Viewer` – ugyanaz a komponens, ugyanaz a lazy
 * chunk, ugyanaz a modál és mérés, csak `variant="button"` módban, hogy a
 * kártya saját gombja nyissa (különben két kép és két gomb lenne egy kártyán).
 * Új viewer NEM készült.
 *
 * Az adat forrása végig meglévő: `constants/stores.js` (cím, térkép-link),
 * `data/uzlet-galeria.json` (külső fotó + alt), `data/store360.json` (panoráma).
 */

// A kártyakép desktopon ~560 px széles (2 hasáb, max-w-5xl), mobilon teljes szélesség.
const CARD_SIZES = '(min-width: 768px) 560px, 92vw';

const DESCRIPTIONS = {
  belvaros: 'A belvárosban, a Rákóczi úton. Itt a pultnál veheted kézbe a készülékeket, és itt adhatod le a javítást is.',
  tesco: 'A Tesco üzletsoron, a főbejárattól balra az első üzlet. Hosszabb nyitvatartás, parkolás a Tesco parkolójában.',
};

const tourFor = (key) =>
  (store360.tours || []).find((t) => t.storeId === key && t.panoramaImage) || null;

const StoreCard = ({ storeKey }) => {
  const cfg = STORES[storeKey];
  const photo = gallery.stores?.[storeKey]?.photos?.kulso;
  const tour = tourFor(storeKey);
  if (!cfg || !photo?.file) return null;

  const v = IMAGE_VARIANTS[photo.file] || {};
  const label = gallery.stores[storeKey].label;

  return (
    <article
      className="flex flex-col rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow"
      data-testid={`store-showcase-${storeKey}`}
    >
      <div className="relative">
        <picture>
          {v.avif && <source type="image/avif" srcSet={v.avif} sizes={CARD_SIZES} />}
          {v.webp && <source type="image/webp" srcSet={v.webp} sizes={CARD_SIZES} />}
          <img
            src={photo.file}
            alt={photo.alt}
            loading="lazy"
            decoding="async"
            width="1600"
            height="1200"
            sizes={CARD_SIZES}
            className="w-full aspect-[4/3] object-cover"
          />
        </picture>
        {tour && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ink/80 text-white text-xs font-bold">
            <Rotate3D size={13} aria-hidden="true" /> 360° bejárás
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-grow">
        <h3 className="text-lg font-bold text-ink">{label}</h3>

        {/* A cím EGYBEN az útvonaltervező link – nem külön elem, de mérhető. */}
        <a
          href={cfg.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackDirectionsClick(`fooldal_uzletek_${storeKey}`)}
          aria-label={`Útvonaltervezés ide: ${label}, ${storeAddressDisplay(cfg)} – megnyitás új lapon`}
          data-testid={`store-showcase-directions-${storeKey}`}
          className="mt-1 inline-flex items-start gap-1.5 text-sm text-gray-600 hover:text-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded"
        >
          <MapPin size={15} className="text-brand-hover flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>{storeAddressDisplay(cfg)}</span>
          <Navigation size={13} className="flex-shrink-0 mt-0.5 opacity-60" aria-hidden="true" />
        </a>

        <p className="mt-3 text-sm text-gray-600 leading-relaxed flex-grow">
          {DESCRIPTIONS[storeKey]}
        </p>

        {/* A gomb a MÁR MEGLÉVŐ 360 viewert nyitja (variant="button"). */}
        {tour ? (
          <div
            className="mt-5"
            onClickCapture={() => trackStoreGalleryClick(storeKey, 'fooldal')}
          >
            <Store360Viewer
              variant="button"
              triggerLabel="Üzlet megtekintése"
              panoramaImage={tour.panoramaImage}
              storeId={tour.storeId}
              storeName={tour.storeName}
              title={tour.title}
              triggerClassName="inline-flex items-center justify-center gap-2 w-full min-h-[48px] px-5 py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 transition-colors"
            />
          </div>
        ) : (
          <Link
            to="/uzletek/"
            onClick={() => trackStoreGalleryClick(storeKey, 'fooldal')}
            className="mt-5 inline-flex items-center justify-center gap-2 w-full min-h-[48px] px-5 py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
          >
            Üzlet megtekintése <ChevronRight size={18} aria-hidden="true" />
          </Link>
        )}
      </div>
    </article>
  );
};

export default function StoreShowcase({ storeKeys = ['belvaros', 'tesco'], className = '' }) {
  const ref = useRef(null);

  // Láthatóság-mérés: a DOM-jelenlét nem jelenti, hogy a látogató LÁTTA is.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        trackStoreGalleryVisible('fooldal');
        io.disconnect();
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const cards = storeKeys.filter((k) => STORES[k] && gallery.stores?.[k]?.photos?.kulso?.file);
  if (!cards.length) return null;

  return (
    <section
      ref={ref}
      className={`py-14 sm:py-16 ${className}`}
      data-testid="store-showcase"
      aria-labelledby="uzleteink-cim"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-10">
          <h2 id="uzleteink-cim" className="text-2xl sm:text-3xl font-bold text-ink mb-3">
            Nézz be üzleteinkbe
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-md mx-auto">
            Két valódi orosházi üzlet. Nem webshop. Gyere be személyesen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {cards.map((key) => <StoreCard key={key} storeKey={key} />)}
        </div>
      </div>
    </section>
  );
}
